/**
 * A COARSE PLAUSIBILITY FLOOR ON THE ROUND-13 SELECT CLAMP — **not** a
 * freshness test. @codex PR #794 round 15 P2, corrected in round 16 P2 (#804).
 *
 * # What it guards
 *
 * `wcsBoundsText.ts::mtextBoxSize` reports two right edges. `paintedRight` is
 * the contents walk (`MAX_GLYPH_ADVANCE` per glyph — a BOUND, right for a
 * prefilter). `selectPaintedRight` is the EXACT select ring's edge, and round
 * 13 made it `min(walkedRight, group 42)` because a worst-case bound may not
 * become exact crossing geometry.
 *
 * That `min` has a PRECONDITION: group 42 must describe the CURRENT layout. A
 * STALE group 42 truncates the exact ring to an old, narrower width while the
 * renderer paints the current string, and a crossing window over the painted
 * suffix MISSES the entity — the round-13 defect, mirrored on the same axis.
 *
 * # WHAT THIS GUARD ACTUALLY COVERS — read this before relying on it
 *
 * Round 15 introduced it with the claim that it made the clamp safe "whatever
 * staled the number". **That was an overstatement, and round 16 P2 is the
 * correction.** The test is
 *
 *     group 42 < glyphs × {@link MIN_GLYPH_ADVANCE} × nominalHeight
 *
 * so it detects exactly one thing: a group 42 that is IMPLAUSIBLY SMALL for
 * the content being walked. It says nothing about whether the number is a
 * measurement of THIS layout — a number can be perfectly plausible and still
 * be a measurement of a different string, a different font or a different
 * size. It tests POSSIBILITY, not FRESHNESS.
 *
 * ## The worked counter-example, which is the #804 defect itself
 *
 * The round-13 witness: 36 glyphs at the 2.5 nominal, AutoCAD's own column at
 * group 42 = 50, the three bundled atlases painting 26-31.
 *
 * | | `nominalHeight` | group 42 | floor = `36 × 0.11 × h` | verdict |
 * |---|---|---|---|---|
 * | as imported | 2.5 | 50 | 9.9 | plausible — correctly |
 * | after a ×2 SCALE, 42 left behind | 5 | 50 | **19.8** | **plausible — WRONG** |
 * | after a ×10 SCALE, 42 left behind | 25 | 50 | 99 | implausible — caught |
 *
 * At ×2 the floor is 19.8 and the stale 50 sails over it, so the guard votes
 * FRESH, the ring clamps at 50, and the bundled rendering — 52-62 units at the
 * doubled height — paints past it. A crossing window over the visible suffix
 * misses, which is the defect the guard was supposed to be the backstop for.
 * Only the ×10 case crosses the floor, and only because that factor is
 * unusually large: the floor is FOUR TIMES below a realistic measurement
 * (0.11 against the atlases' 0.29-0.34 heights per glyph), so it takes roughly
 * a ×4 or larger staleness ratio before it fires at all.
 *
 * ## So: what does close the gap
 *
 * The WRITE paths, one per writer, and they are what must be kept correct:
 *
 *   * the ENTITY edit (`properties/apply`) — round 14
 *     (`command-engine::mtextLayoutMeasurementInvalidation`);
 *   * the STYLE TABLE edit (`header/update-text-style`) — round 15
 *     (`command-engine::mtextStyleMeasurementInvalidation`);
 *   * SCALE — round 16, #804 (`command-engine::scaleEntityRadii`, which
 *     SCALES the pair with the block where the scaled block stays
 *     geometrically SIMILAR and CLEARS it where an absolute inline `\\H`
 *     breaks that — `mtextScaleSimilarity.ts`).
 *
 * KEEP THIS GUARD ANYWAY. It is cheap, it is a genuine last line against a
 * grossly wrong number from a writer nobody has thought of yet or from a
 * malformed file, and it costs at most a looser ring when it misfires. But it
 * is NOT a substitute for fixing writers, and it may not be cited as one: the
 * three rounds above exist because each writer had to be fixed at the writer.
 *
 * Two of those three are writer-COMPLETE by construction —
 * `MTEXT_FIELD_AFFECTS_LAYOUT` and `TEXT_STYLE_FIELD_AFFECTS_LAYOUT` are
 * mapped types over `keyof`, so a new schema field is a COMPILE ERROR until it
 * is classified. The geometric transforms are not: that set was enumerated by
 * hand in round 16 and nothing fails when a fourth writer appears. #809 is the
 * durability follow-up — a command census test, in the pattern of
 * `sideEffectMutators.census.test.ts`, asserting that every command whose
 * reducer can change an MTEXT's layout-affecting fields either clears groups
 * 42/43 or transforms them correctly. A stored layout fingerprint was assessed
 * in the same round and REJECTED; #809 carries why.
 *
 * It also cannot touch the OTHER facet at all. A stale group 42 is
 * {@link mtextBoxSize}'s SEMANTIC rectangle width, taken VERBATIM — refusing
 * to clamp only widens the select ring, it cannot make that rectangle stop
 * stating a measurement of a string the entity no longer holds.
 *
 * # The direction it errs in
 *
 * `group 42 < lowerBound(contents)` ⇒ group 42 CANNOT be a measurement of this
 * content ⇒ do not clamp; fall back to `walkedRight`, which is round 13's
 * PRE-clamp behaviour — looser, but it never under-reports, and BOUNDS ⊇ SELECT
 * still holds because bounds keep the walk on every branch.
 *
 * The two ways to be wrong are NOT symmetric, and the guard is built for that:
 *
 *   * a false STALE verdict (the measurement was fine, the bound over-reached)
 *     costs one entity a looser exact ring — the state the whole repo was in
 *     before round 13, on that one entity;
 *   * a false FRESH verdict (the measurement is stale and the bound let it
 *     through) re-opens the finding — and, per the table above, that is the
 *     COMMON case rather than a corner.
 *
 * So the bound must be a genuine LOWER bound and is rounded DOWN, the same
 * discipline {@link MAX_GLYPH_ADVANCE} is rounded UP with.
 */

/**
 * The per-glyph pen advance FLOOR, as a fraction of the run height: the
 * NARROWEST step any glyph of any BUNDLED font moves the cursor by. The exact
 * mirror of `wcsBoundsText.ts::MAX_GLYPH_ADVANCE`, derived from the same files
 * by the same method and re-measured beside it in
 * `apps/web/…/text/__tests__/mtextGlyphMetricBound.test.ts`.
 *
 * MEASURED over every glyph of the three atlases the app ships
 * (`apps/web/public/sdf-atlases/*.json`, 230 glyphs each), as
 * `advance / lineHeight` — the renderer's own ratio:
 *
 * | atlas | lineHeight | narrowest glyph | advance / lineHeight |
 * |---|---|---|---|
 * | `Roboto-Regular` | 55 | `'` | 7/55 = 0.12727 |
 * | `OpenGost-TypeA` | 42 | `'` | 5/42 = 0.11905 |
 * | `OpenGost-TypeB` | 42 | `'` | 6/42 = 0.14286 |
 *
 * `0.11905` rounded DOWN to the hundredth. No bundled glyph advances zero, so
 * the floor is a real positive number rather than a vacuous one; the two
 * missing-glyph fallbacks are ABOVE it too (`lineHeight × 0.5` on the instanced
 * path, `(above + below) / 2` on the SHX one).
 *
 * It does NOT floor a REGISTERED SHX font, for the same reason
 * {@link MAX_GLYPH_ADVANCE} does not bound one (`wcsBoundsText.ts` module
 * header U1 / #795): `fontRegistry.ts` resolves SHX names against
 * user-supplied bytes. A registered face narrower than 0.11 heights per glyph
 * would make this bound over-reach — which is the FALSE-STALE direction, i.e.
 * a looser ring, never a missed glyph.
 */
export const MIN_GLYPH_ADVANCE = 0.11;

/**
 * COULD `horizontalWidth` (group 42) be AutoCAD's measurement of the content
 * the walk just measured? `false` ⇒ the caller must NOT clamp to it.
 *
 * "Could", not "is" — `true` means only that no lower bound rules the number
 * out, and the module header's ×2 table shows how routinely a stale value
 * earns a `true`. Do not read a `true` as "this measurement is fresh".
 *
 * `lineAdvanceFactor` is the painted-run walk's own pen travel for the widest
 * line, in units of `nominalHeight` (`Σ over that line's runs of glyphs ×
 * runHeight × widthFactor × tracking`) — the SAME factor
 * `wcsBoundsText.ts::mtextPaintedBound` multiplies by {@link MAX_GLYPH_ADVANCE}
 * for the upper bound. Multiplying it by {@link MIN_GLYPH_ADVANCE} instead
 * evaluates that identical walk at the narrowest plausible glyph, which is a
 * LOWER bound on the pen travel any font can produce for this content. It is
 * one multiplication over numbers the walk already produced — not a second
 * derivation of the interval (the rounds-7/8 correlation rule).
 *
 * `true` for every input the guard cannot judge, so it only ever REFUSES a
 * clamp on positive evidence:
 *
 *   * a non-finite or non-positive floor (`nominalHeight` clamped to 0, an
 *     overflowed inline `\\H`, a `\\W0;` run) is no evidence at all — the
 *     comparison below is written so `NaN` yields `true`;
 *   * the floor counts every decoded glyph, TRAILING SPACES included, while
 *     AutoCAD may measure only to the last ink. At 0.11 heights a space the
 *     measurement omits is a small over-reach of the floor, and it errs into
 *     the false-STALE direction.
 */
export function mtextCachedWidthCouldDescribeContents(
  horizontalWidth: number,
  lineAdvanceFactor: number,
  nominalHeight: number,
): boolean {
  const floor = lineAdvanceFactor * MIN_GLYPH_ADVANCE * nominalHeight;
  if (!Number.isFinite(floor) || floor <= 0) return true;
  return horizontalWidth >= floor;
}
